/**
 * The practice yard's lesson plan, as a pure state machine.
 *
 * The view reports WHAT HAPPENED (`TutorialEvent`); this decides whether that
 * finishes a step. Keeping the rules here means the order, the counts and the
 * "does this event count right now" logic are testable, and the view only has
 * to emit facts.
 *
 * Wording rule (Janeks): short and plain, for players whose first language is
 * not English. One idea per line, concrete verbs, no idioms, no slang.
 */

export type TutorialStepId = 'move' | 'melee' | 'shoot' | 'loot' | 'equip' | 'map';

export interface TutorialStepDef {
  id: TutorialStepId;
  /** Short heading, 1-3 words. */
  title: string;
  /** What to do, one sentence. */
  goal: string;
  /** Extra lines — keys and tips. Each one short. */
  tips: string[];
  /** Hits / items needed to finish; 1 for single-action steps. */
  count: number;
}

export const TUTORIAL_STEPS: readonly TutorialStepDef[] = [
  {
    id: 'move', title: 'Move', count: 1,
    goal: 'Walk to the green circle.',
    tips: ['Use W A S D or the arrow keys.', 'Drag with the left mouse button to turn the camera.', 'CapsLock switches between walking and running.'],
  },
  {
    id: 'melee', title: 'Hit with a knife', count: 3,
    goal: 'Hit the straw dummy 3 times with your knife.',
    tips: ['Hold the right mouse button to aim.', 'Then click the left mouse button, or press Space.', 'Stand close. A knife is short.'],
  },
  {
    id: 'shoot', title: 'Shoot', count: 3,
    goal: 'Shoot the far targets 3 times.',
    tips: ['Press 2 to take out the pistol. Press 1 for the knife.', 'Hold the right mouse button to aim, then click or press Space.', 'Each shot uses 1 bullet.'],
  },
  {
    id: 'loot', title: 'Search', count: 1,
    goal: 'Walk to the crate and press E.',
    tips: ['Containers can hold useful things.', 'Stand next to one to search it.'],
  },
  {
    id: 'equip', title: 'Wear it', count: 1,
    goal: 'Open your gear and put on what you found.',
    tips: ['Press I, or use the Gear button.', 'Click the item, then click Wear.'],
  },
  {
    id: 'map', title: 'The map', count: 1,
    goal: 'Read how the map works.',
    tips: ['Press M in the settlement to open the map.'],
  },
];

export type TutorialEvent =
  | { type: 'reached-marker' }
  | { type: 'melee-hit' }
  | { type: 'shot-hit' }
  | { type: 'looted' }
  | { type: 'equipped' }
  | { type: 'map-read' };

const EVENT_FOR_STEP: Record<TutorialStepId, TutorialEvent['type']> = {
  move: 'reached-marker',
  melee: 'melee-hit',
  shoot: 'shot-hit',
  loot: 'looted',
  equip: 'equipped',
  map: 'map-read',
};

export interface TutorialProgress {
  /** Index into `TUTORIAL_STEPS`; equals the length when finished. */
  index: number;
  /** Progress inside the current step, 0..count. */
  done: number;
}

export const START: TutorialProgress = { index: 0, done: 0 };

export function isFinished(p: TutorialProgress): boolean {
  return p.index >= TUTORIAL_STEPS.length;
}

export function currentStep(p: TutorialProgress): TutorialStepDef | null {
  return TUTORIAL_STEPS[p.index] ?? null;
}

/**
 * Applies one event. Events for a step that is not current are ignored, so
 * hitting a dummy early or searching the crate ahead of time never skips a
 * lesson — the player is simply told what to do now.
 */
export function advance(p: TutorialProgress, e: TutorialEvent): TutorialProgress {
  const step = currentStep(p);
  if (!step || EVENT_FOR_STEP[step.id] !== e.type) return p;
  const done = p.done + 1;
  return done >= step.count ? { index: p.index + 1, done: 0 } : { index: p.index, done };
}
